import React, { useState, useRef, useCallback } from 'react';
import { CloudArrowUpIcon, XMarkIcon, PhotoIcon, LinkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { backendImageService } from '../../services/backendImageService';
import { API_BASE_URL } from '../../config/apiConfig';

interface ImageUploadProps {
  onImageUpload: (imageUrl: string, file?: File) => void;
  currentImageUrl?: string;
  aspectRatio?: 'square' | 'landscape' | 'portrait' | 'auto';
  maxSize?: number; // in MB
  acceptedFormats?: string;
  category?: string;
  placeholder?: string;
  className?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageUpload,
  currentImageUrl,
  aspectRatio = 'auto',
  maxSize = 10, // 10MB default
  acceptedFormats = 'image/*',
  category = 'general',
  placeholder = 'Click to upload or drag and drop',
  className = '',
}) => {
  const { actualTheme } = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (file: File) => {
    setError(null);
    
    // Validate file type (allow any image type)
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File too large. Maximum size: ${maxSize}MB`);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Logic for progress simulation (Cloudinary upload_stream doesn't easily provide progress on client side without XHR)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Upload to Cloudinary via backend proxy
      const result = await backendImageService.uploadImage(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Use the uploaded URL
      onImageUpload(result.url, file);
      
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);

    } catch (err: any) {
      setError(err.message || 'Failed to upload image. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [maxSize, onImageUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleClick = useCallback(() => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  }, [isUploading]);

  const handleRemoveImage = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Auto-delete discarded Cloudinary files
    if (currentImageUrl && currentImageUrl.includes('res.cloudinary.com')) {
      try {
        const urlParts = currentImageUrl.split('/');
        const filePart = urlParts[urlParts.length - 1];
        const publicId = filePart.split('.')[0];
        
        await fetch(`${API_BASE_URL}/upload?public_id=${publicId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        console.log('🗑️ Successfully deleted discarded image from Cloudinary:', publicId);
      } catch (err) {
        console.error('Failed to auto-delete discarded image from Cloudinary:', err);
      }
    }
    
    onImageUpload('');
  }, [onImageUpload, currentImageUrl]);

  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case 'square': return 'aspect-square';
      case 'landscape': return 'aspect-video';
      case 'portrait': return 'aspect-[3/4]';
      default: return '';
    }
  };

  return (
    <div className={`w-full ${className}`}>


      <div
        className={`
          relative border-2 border-dashed rounded-2xl p-4 text-center cursor-pointer transition-all duration-300
          w-full h-full flex items-center justify-center overflow-hidden
          ${isDragOver 
            ? 'border-primary-500 bg-primary-500/5'
            : actualTheme === 'dark'
              ? 'border-slate-700 hover:border-slate-500 bg-slate-800/30'
              : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
          }
          ${isUploading ? 'cursor-not-allowed opacity-75' : 'hover:shadow-lg hover:shadow-primary-500/5'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={isUploading}
      />

        {currentImageUrl && !isUploading ? (
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={currentImageUrl}
              alt="Preview"
              className="max-w-full max-h-full object-contain rounded-xl"
            />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all shadow-lg active:scale-95"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center w-full">
            {isUploading ? (
              <div className="w-full px-4">
                <div className="w-10 h-10 mx-auto mb-3">
                  <div className="w-full h-full border-4 border-primary-100 border-t-primary-600 rounded-full animate-spin shadow-inner"></div>
                </div>
                <p className={`text-xs font-bold uppercase tracking-widest ${actualTheme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                  {uploadProgress}%
                </p>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 mt-3 overflow-hidden">
                  <div 
                    className="bg-primary-600 h-full transition-all duration-300 shadow-[0_0_10px_rgba(37,99,235,0.5)]"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            ) : (
              <>
                <div className={`w-10 h-10 mb-3 ${
                  actualTheme === 'dark' ? 'text-slate-600' : 'text-slate-300'
                }`}>
                  {isDragOver ? (
                    <CloudArrowUpIcon className="w-full h-full animate-bounce" />
                  ) : (
                    <PhotoIcon className="w-full h-full" />
                  )}
                </div>
                <p className={`text-xs font-bold ${
                  actualTheme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  {isDragOver ? 'Drop here' : placeholder}
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-bold rounded-xl animate-in shake duration-300">
          ⚠️ {error}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
